import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

/**
 * (07) PHILOSOPHY — single, confident statement of Parabolum.
 *
 * No effects. No GSAP. No particles. No black hole.
 * Huge typography, generous whitespace, one quiet reveal on enter.
 *
 * Voice: "We already know what works." — boutique, established,
 * editorial. Not "look what we can do".
 */
function Reveal({ children, active, delay = 0, className = "" }) {
  return (
    <span className="reveal-mask block">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={active ? { y: "0%" } : { y: "110%" }}
        transition={{ duration: 0.9, ease, delay }}
      >
        <span className={className}>{children}</span>
      </motion.span>
    </span>
  );
}

const PILLARS = [
  {
    n: "01",
    title: "Clarity",
    body:
      "Every decision is deliberate. Nothing exists because it could — only because it must.",
  },
  {
    n: "02",
    title: "Execution",
    body:
      "Direct, focused, uncompromising. Built by the people who designed it.",
  },
  {
    n: "03",
    title: "Longevity",
    body:
      "Products that stay sharp years later. Designed to impress. Engineered to remain.",
  },
];

export default function Philosophy() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15%" });

  return (
    <section
      ref={ref}
      id="philosophy"
      data-testid="philosophy-section"
      className="relative overflow-hidden bg-[var(--paper)] py-32 text-[var(--ink)] md:py-44"
    >
      <div className="relative mx-auto max-w-[1500px] px-5 md:px-10">
        <span className="ff-ui label text-[var(--ink-soft)]">
          (07) — Philosophy
        </span>

        <h2 className="ff-display mt-10 text-5xl font-extrabold leading-[0.94] tracking-tight md:mt-14 md:text-8xl">
          <Reveal active={inView}>We don&apos;t build websites.</Reveal>
          <Reveal active={inView} delay={0.08} className="text-[var(--ink-soft)]">
            We build products people remember.
          </Reveal>
        </h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease, delay: 0.3 }}
          className="mt-24 grid gap-12 border-t border-[var(--line)] pt-14 md:mt-32 md:grid-cols-3 md:gap-16 md:pt-20"
        >
          {PILLARS.map((p) => (
            <div key={p.n} className="flex flex-col gap-5">
              <span className="ff-ui text-[12px] font-medium tracking-[0.28em] text-[var(--ink-faint)]">
                {p.n}
              </span>
              <h3 className="ff-display text-3xl font-extrabold tracking-tight text-[var(--ink)] md:text-4xl">
                {p.title}
              </h3>
              <p className="max-w-xs text-[14px] leading-relaxed text-[var(--ink-soft)]">
                {p.body}
              </p>
            </div>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.9, ease, delay: 0.6 }}
          className="ff-serif mt-24 max-w-md text-xl leading-snug text-[var(--ink-soft)] md:mt-32 md:text-2xl"
        >
          Designed to impress.
          <br />
          Engineered to remain.
        </motion.p>
      </div>
    </section>
  );
}
